import React, { PropsWithChildren } from 'react';
import { connect } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { Clip } from '@ff-os/rendering-shared/Clip';
import { Spaces } from '@ff-os/rendering-shared/coordinates';
import { AspectRatio } from '@ff-os/shared-react/components/aspect-ratio/AspectRatio';
import { Translate } from '@ff-os/shared-utils/types/Translate';
import { Prefab } from '@ff-resource-plugins/prefabs-shared/Prefab';
import { GiftPdpState } from '@ff-scene-plugins/gift-redux/store';
import { Paintable } from 'ff-canvas';
import { selectPaintablesForPrefabPreviewById, selectPrefabViewboxById } from '../_redux-prefabs';
import { selectPrefabBackgroundAlpha } from '../_redux-scene';
import { selectBackgroundPaintableByVariantId, selectSpacesForFullPreview, selectVariantPreviewClip } from '../_redux-variants';
import { Preview2d } from './Preview2d';

const _FullPrefabPreview2d = ({ paintables, spaces, previewClip }: Props) => (
    <AspectRatio width={spaces.sceneWidth} height={spaces.sceneHeight}>
        <Preview2d paintables={paintables} spaces={spaces} previewClip={previewClip} />
    </AspectRatio>
);

type Props = PropsWithChildren<{
    paintables: Paintable[];
    spaces: Spaces;
    previewClip: Clip | undefined;
}>;

const mapStateToProps = (
    state: GiftPdpState,
    ownProps: { prefab: Prefab; t: Translate }
) => ({
    paintables: selectPaintablesForFullPrefabPreviewById(state, ownProps.prefab.id, ownProps.t),
    spaces: selectSpacesForFullPrefabPreviewById(state, ownProps.prefab.id),
    previewClip: selectVariantPreviewClip(state)
});

export const FullPrefabPreview2d = connect(
    mapStateToProps
)(_FullPrefabPreview2d);

const selectSpacesForFullPrefabPreviewById = createSelector([
    state => state,
    selectPrefabViewboxById
], selectSpacesForFullPreview);

const selectPaintablesForFullPrefabPreviewById = createSelector([
    selectPaintablesForPrefabPreviewById,
    (state: GiftPdpState, prefabId: string) => selectPrefabBackgroundPaintable(state, prefabId)
], (paintables: Paintable[], background: Paintable) => paintables.concat(background));

const selectPrefabBackgroundPaintable = createSelector(
    (state: GiftPdpState, prefabId: string) => selectBackgroundPaintable(state, prefabId),
    (state: GiftPdpState) => selectPrefabBackgroundAlpha(state),
    (background, alpha) => ({ ...background, alpha }));

const selectBackgroundPaintable = createSelector([
    state => state,
    (state: GiftPdpState, prefabId: string) => selectPrefabViewboxById(state, prefabId)
], (state, viewbox) => selectBackgroundPaintableByVariantId(state, viewbox));